/**
 * GUPM-176 / GUPM-213 — Baixa técnica: validação + enquadramento ANM. Domínio puro.
 * GUPM-186 (review #100): a regra "sem barragem → sem enquadramento ANM" mora aqui.
 * GUPM-213: o plano de ação vira lista de AÇÕES estruturadas — a validação exige
 * ≥1 ação válida quando o RT marca "definir ações".
 * GUPM-294: declarar nível ANM NÃO obriga plano. Este cabeçalho dizia "quando há
 * enquadramento ANM (ou …)" — o ramo do enquadramento é justamente o que saiu.
 */
import type { NivelAnmDeclaravel } from '../estrutura/status';
import { acoesValidas, faltasNaAcao, type AcaoDraft } from '../planos/acoes';

export type EnquadramentoAnm = 'NE-1' | 'NE-2' | 'NE-3';

export const PARECER_MIN = 30;

export interface BaixaInput {
  parecer: string;
  enquadramento: EnquadramentoAnm | null;
  /**
   * GUPM-186 (review #100): a Res. ANM 220 é sobre a ESTRUTURA. Enquadrar exige uma
   * barragem definida — sem `dam_id` não há o que declarar.
   */
  damId: string | null;
  /** "Sim, definir ações" (Bloco 3) — o RT vai registrar um plano de ação. */
  planoAplica: boolean;
  /** Ações do plano (GUPM-213) — o plano de ação estruturado da baixa. */
  acoes: AcaoDraft[];
  /** Vincular a um plano JÁ existente da TARP em vez de criar (GUPM-213 Extra 2). */
  planoExistenteId: string | null;
}

/** Enquadramento ANM (Res. 220) só é possível com uma barragem definida — a norma é sobre a ESTRUTURA. */
export function podeEnquadrarAnm(damId: string | null): boolean {
  return damId !== null && damId.trim().length > 0;
}

/**
 * Valida a baixa; retorna erros ([] = ok). Parecer ≥30; enquadramento exige barragem
 * definida (Res. 220); plano de ação exige ≥1 ação com descrição **quando o RT diz que
 * se aplica** — fiel ao `closeTarpFinal` do V8 (GUPM-213). Declarar nível ANM não
 * obriga plano (GUPM-294).
 */
export function validateBaixa(input: BaixaInput): string[] {
  const errors: string[] = [];
  if (input.parecer.trim().length < PARECER_MIN) {
    errors.push(`O parecer precisa de ao menos ${PARECER_MIN} caracteres.`);
  }
  if (input.enquadramento && !podeEnquadrarAnm(input.damId)) {
    errors.push('Enquadramento ANM exige uma barragem definida (a Res. 220 é sobre a estrutura).');
  }
  // Plano de ação = criar novo (≥1 ação) OU vincular um já existente da TARP (Extra 2).
  const temPlano = input.planoExistenteId !== null || acoesValidas(input.acoes).length >= 1;
  // GUPM-294: quem obriga o plano é a ESCOLHA DO RT (`planoAplica`), não o
  // enquadramento ANM. Havia aqui um ramo `enquadramento && !temPlano` que barrava a
  // baixa mesmo com "Não se aplica" marcado. Ele não tinha ADR, não está no protótipo
  // canônico V8 e o comentário que o justificava atribuía à Res. 220 uma exigência de
  // plano de ação que NÃO foi verificada em nenhuma fonte deste repositório. Removê-lo
  // não afirma o contrário — afirma que a exigência não estava justificada.
  // A regra da Res. 220 que ESTÁ verificada (enquadrar é sobre a estrutura, logo exige
  // barragem definida) segue acima, intacta.
  if (input.planoAplica) {
    // GUPM-295 (adendo): o bloqueio precisa NOMEAR o campo que falta. Uma ação começada
    // e incompleta bloqueia mesmo havendo outra completa — senão `acoesValidas` (filtro,
    // não `every`) a descartaria e o plano seria criado SEM ela, sem aviso ao RT.
    const faltas = new Set(input.acoes.flatMap(faltasNaAcao));
    if (faltas.has('prazo')) {
      errors.push('Defina a data máxima de conclusão de cada ação do plano.');
    }
    if (faltas.has('responsavel')) {
      errors.push('Defina o responsável de cada ação do plano.');
    }
    if (faltas.size === 0 && !temPlano) {
      errors.push('Adicione ao menos 1 ação ao plano de ação (ou marque "Não se aplica").');
    }
  }
  return errors;
}

const NE_TO_NIVEL: Record<EnquadramentoAnm, NivelAnmDeclaravel> = {
  'NE-1': 'ATENCAO',
  'NE-2': 'ALERTA',
  'NE-3': 'CRITICO',
};

/** Nível de estrutura declarado por um enquadramento ANM (Res. 220). */
export function nivelEstruturaFromEnquadramento(e: EnquadramentoAnm): NivelAnmDeclaravel {
  return NE_TO_NIVEL[e];
}

export const ENQUADRAMENTO_LABEL: Record<EnquadramentoAnm, string> = {
  'NE-1': 'NE-1 — Atenção',
  'NE-2': 'NE-2 — Alerta',
  'NE-3': 'NE-3 — Crítico',
};

export const ENQUADRAMENTOS: EnquadramentoAnm[] = ['NE-1', 'NE-2', 'NE-3'];

/**
 * Nível do design system (`LEVEL_UI`/`TarpLevel`) de cada enquadramento ANM — usado
 * para colorir os botões de nível na baixa E para definir `Plano.nivel` do plano criado.
 * Distinto de `NE_TO_NIVEL` (NivelAnmDeclaravel, com CRITICO): aqui NE-3 → EMERGENCIA
 * porque `TarpLevel` não tem CRITICO. Constante única (review #124) — não duplicar na UI.
 */
export const NE_TO_TARP_LEVEL: Record<EnquadramentoAnm, 'ATENCAO' | 'ALERTA' | 'EMERGENCIA'> = {
  'NE-1': 'ATENCAO',
  'NE-2': 'ALERTA',
  'NE-3': 'EMERGENCIA',
};
